import { useHistory } from 'react-router-dom';

import { HASHES, FULL_PATHS } from '../constants';
import { useStatementSelection } from '.';

/**
 * Returns a LocationDescriptor that can be used with react-router-dom to send the user to the
 * Manage Statements modal. If they do not have any statements uploaded, the modal will be opened
 * on the "Uploads" tab. Otherwise, the modal will be opened on the "Complete" tab.
 *
 * Note that the Manage Statements modal lives in a child path of the Statements page, which is
 * viewed in either `charts` mode or `table` mode.
 *
 * A LocationDescriptor returned by this function looks like:
 *  ```
 *  {
 *      hash: '#complete',  // `hash` sets the initial tab of the Manage Statements modal
 *      pathname: '/statements/charts/manage',  // `pathname` must specify the /charts or /table subpath
 *      search: '?entity_id=AS01558069&statement_ids=1617756547AS0155806920202W', // `search` is already set on the location based on entity / statement selection
 *  }
 *  ```
 *
 * Which results in a final path like:
 *  ```
 *  '/statements/charts/manage?entity_id=AS01558069&statement_ids=1617756547AS0155806920202W#complete'
 *  ```
 */
export const useManageStatementsLocation =
    (): import('history').LocationDescriptor => {
        const history = useHistory();
        const {
            statementSelectionProps: { hasStatements },
        } = useStatementSelection();
        const hash = hasStatements
            ? HASHES.MANAGE_STATEMENTS.COMPLETE
            : HASHES.MANAGE_STATEMENTS.UPLOADS;

        let pathname = FULL_PATHS.MANAGE_STATEMENTS_CHARTS;

        if (history.location.pathname.includes(FULL_PATHS.STATEMENTS_TABLE)) {
            pathname = FULL_PATHS.MANAGE_STATEMENTS_TABLE;
        }

        return {
            hash,
            pathname,
            search: history.location.search,
        };
    };
